import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import { useSignupModalStyles as usePartnerSignupStyles } from '@/auth/components/shared/useSignupModalStyles';
import { darkBrown } from '@/theme/colors';

import { AccountStep } from './components/AccountStep';
import { ConfirmExitDialog } from './components/ConfirmExitDialog';
import { ConfirmationStep } from './components/ConfirmationStep';
import { EmailOtpStep } from './components/EmailOtpStep';
import { InviteAcceptStep } from './components/InviteAcceptStep';
import type { CompleteSMBSignupInput } from './components/SubscriptionStep';
import { TeamJoinStep } from './components/TeamJoinStep';
import { TeamRequestSentStep } from './components/TeamRequestSentStep';
import { TeamStep } from './components/TeamStep';
import { useDomainMatch } from './hooks/useDomainMatch';
import { useOtpExpiryTimer } from './hooks/useOtpExpiryTimer';
import { usePendingProjectInvite } from './hooks/usePendingProjectInvite';
import { useSelfServiceSMBSignupFlow } from './hooks/useSelfServiceSMBSignupFlow';
import type {
  AccountData,
  DomainMatch,
  PendingProjectInvite,
  SelfServiceSMBSignupState,
  SelfServiceSMBSignupStep,
  SubscriptionResult,
} from './types';
import { trackSelfServiceSMBSignupStep } from './utils/analytics';
import { captureSelfServiceError } from './utils/sentryScrub';

const SubscriptionStep = lazy(() =>
  import('./components/SubscriptionStep').then((m) => ({ default: m.SubscriptionStep }))
);

export interface SelfServiceSMBSignupModalProps {
  open: boolean;
  onClose?: () => void;
  onGoToDashboard: () => void;
  // ENG-5334 follow-up: distinct destination for SubscriptionStep's
  // active-membership guard dialogs — expert requests, not the dashboard.
  onGoToProjects: () => void;
  initialState?: Partial<SelfServiceSMBSignupState>;
  trialDays?: number;
  seatCount?: number;
  // Wraps createUser(signup_subtype: "self_service"). Resolves on success.
  // ENG-5201's phoneless pivot: this replaces the prior onSendOtp/onVerifyOtp
  // SMS auth pair — the user is created upfront, then verified via email-OTP.
  onSignup: (data: AccountData) => Promise<void>;
  // Email-OTP send/verify (transport: EMAIL on sendPasswordlessOTP /
  // verifyPasswordlessOTP). The verify response carries a token whose claim
  // contains the verified email and is passed to completeSMBSignup as
  // otpVerificationToken.
  onSendEmailOtp: (email: string) => Promise<void>;
  onVerifyEmailOtp: (email: string, code: string) => Promise<{ token: string; expiresAt: number }>;
  // Domain-match fetch with Bearer auth (no OTP-token header) per W-21.
  // The server reads the email domain from the authenticated user record,
  // not from a variable — see useDomainMatch.ts.
  onFetchDomainMatches: () => Promise<DomainMatch[]>;
  onFetchPendingInvite: (otpVerificationToken?: string) => Promise<PendingProjectInvite | null>;
  onFetchActiveProjectMembership: (otpVerificationToken?: string) => Promise<boolean>;
  onAcceptInvite: (token: string) => Promise<void>;
  onRequestToJoin: (input: {
    groupId: string;
    otpVerificationToken?: string;
  }) => Promise<{ autoApproved: boolean }>;
  onCreateSetupIntent: (input: {
    otpVerificationToken?: string;
    companyName?: string;
  }) => Promise<{ clientSecret: string }>;
  onCompleteSMBSignup: (input: CompleteSMBSignupInput) => Promise<SubscriptionResult>;
  // Label shown on the terminal CTA (confirmation + team-request-sent
  // steps). Defaults to "Go to your dashboard"; inline-mount call sites
  // (expert profile, dashboard banner) pass "Continue" so the button
  // accurately reflects where the user lands next.
  ctaLabel?: string;
}

const STEP_TITLE_IDS: Record<SelfServiceSMBSignupStep, string> = {
  account: 'self-service-smb-account-title',
  'email-otp': 'self-service-smb-account-title',
  'invite-accept': 'self-service-smb-invite-accept-title',
  'team-create': 'self-service-smb-team-title',
  'team-join': 'self-service-smb-team-title',
  'team-request-sent': 'self-service-smb-team-title',
  subscription: 'self-service-smb-subscription-title',
  confirmation: 'self-service-smb-confirmation-title',
};

const STEP_HEADER_SUBTITLE: Record<SelfServiceSMBSignupStep, string> = {
  account: 'Step 1 of 2',
  'email-otp': 'Step 1 of 2',
  'invite-accept': 'Step 2 of 2',
  'team-create': 'Step 2 of 2',
  'team-join': 'Step 2 of 2',
  'team-request-sent': '',
  subscription: 'Payment & subscription',
  confirmation: '',
};

function StepFallback() {
  return <div className="text-sm py-12 text-center text-gray-500">Loading…</div>;
}

export function SelfServiceSMBSignupModal({
  open,
  onClose,
  onGoToDashboard,
  onGoToProjects,
  initialState,
  trialDays,
  seatCount = 1,
  onSignup,
  onSendEmailOtp,
  onVerifyEmailOtp,
  onFetchDomainMatches,
  onFetchPendingInvite,
  onFetchActiveProjectMembership,
  onAcceptInvite,
  onRequestToJoin,
  onCreateSetupIntent,
  onCompleteSMBSignup,
  ctaLabel,
}: SelfServiceSMBSignupModalProps): JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const flow = useSelfServiceSMBSignupFlow(initialState);
  const domainMatch = useDomainMatch({
    runQuery: onFetchDomainMatches,
  });
  const pendingProjectInvite = usePendingProjectInvite({
    runQuery: onFetchPendingInvite,
  });

  const handleOtpExpired = useCallback(() => flow.otpExpired(), [flow]);
  useOtpExpiryTimer({
    expiresAt: flow.state.emailOtpExpiresAt,
    onExpired: handleOtpExpired,
  });

  // R-11: on resume directly into team-create / team-join (i.e., we
  // mounted at this step rather than transitioning from email-otp), the
  // domain-match list in state is empty AND the pending-invite check that
  // normally runs in EmailOtpStep never fired. Re-fetch both once, combined
  // into the same resolveEmailOtpVerification dispatch the post-verify path
  // uses (see useSelfServiceSMBSignupFlow.ts), so a resumer with a pending
  // project invite is routed to invite-accept here rather than only being
  // caught by the later completeSMBSignup guard. One combined dispatch (not
  // a second, separate setPendingInvite-style action) keeps step-priority
  // ownership in the single reducer case. Guarded so we only run on the
  // very first entry.
  //
  // ENG-5334 review fix (finding #4): seeded from initialState.step, not
  // unconditionally false. A plain useRef(false) can't distinguish a
  // genuine cross-session resume mount from an in-session transition into
  // team-create/team-join (e.g. declineInvite, or simply the ordinary
  // zero-domain-match path out of resolveEmailOtpVerification), the ref
  // only flipped true after the effect itself ran, so the very first time
  // flow.state.step became team-create/team-join in-session, every guard
  // below already passed and this effect fired as if it were a resume.
  const resumedDomainFetchRef = useRef(
    initialState?.step !== 'team-create' && initialState?.step !== 'team-join'
  );
  useEffect(() => {
    if (resumedDomainFetchRef.current) return;
    if (flow.state.step !== 'team-create' && flow.state.step !== 'team-join') return;
    if (flow.state.domainMatches.length > 0 || flow.state.domainMatchLoading) return;
    const email = flow.state.account.email;
    if (!email) return;
    resumedDomainFetchRef.current = true;
    flow.setDomainMatchLoading(true);
    void (async () => {
      try {
        const [pendingInvite, matches] = await Promise.all([
          pendingProjectInvite.fetchPendingInvite(),
          domainMatch.fetchMatches({ email }),
        ]);
        flow.resolveEmailOtpVerification(pendingInvite, matches);
      } catch (err) {
        // Surface the failure: clear the loading state so the team-create
        // step renders, and report to Sentry with allow-listed context so
        // ops can see resume-path domain-match/pending-invite failures.
        flow.setDomainMatchLoading(false);
        captureSelfServiceError(err, { step: 'domain_match_resume' });
      }
    })();
  }, [
    flow.state.step,
    flow.state.domainMatches.length,
    flow.state.domainMatchLoading,
    flow.state.account.email,
    flow.setDomainMatchLoading,
    flow.resolveEmailOtpVerification,
    domainMatch.fetchMatches,
    pendingProjectInvite.fetchPendingInvite,
  ]);

  // Pre-account (`account`) close is silent — there's nothing yet to resume
  // (no backend record). Post-account close opens a confirm dialog, since
  // the backend has saved the user and abandoning here is a meaningful
  // recoverable state. Terminal steps (`confirmation`, `team-request-sent`)
  // are excluded — there's nothing left to lose, so backdrop click should
  // dismiss directly without a Stay/Leave prompt.
  const isPostAccount = flow.state.step !== 'account';
  const isTerminalStep =
    flow.state.step === 'confirmation' || flow.state.step === 'team-request-sent';
  const dismissibleStep = isPostAccount && !isTerminalStep;
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);

  const handleConfirmExit = useCallback(() => {
    setConfirmExitOpen(false);
    trackSelfServiceSMBSignupStep('abandon', flow.state.step, { reason: 'close' });
    onClose?.();
  }, [flow.state.step, onClose]);

  const handleCancelExit = useCallback(() => setConfirmExitOpen(false), []);

  // Soft close pre-account; confirm-on-close post-account (R-3).
  const handleClose = useCallback(() => {
    if (!dismissibleStep) {
      onClose?.();
      return;
    }
    setConfirmExitOpen(true);
  }, [dismissibleStep, onClose]);

  // Funnel telemetry: emit step_complete for the step we just left, then
  // step_view for the new step.
  const prevStepRef = useRef<SelfServiceSMBSignupStep | null>(null);
  useEffect(() => {
    const prev = prevStepRef.current;
    if (prev && prev !== flow.state.step) {
      trackSelfServiceSMBSignupStep('complete', prev);
    }
    trackSelfServiceSMBSignupStep('view', flow.state.step);
    prevStepRef.current = flow.state.step;
  }, [flow.state.step]);

  const titleId = STEP_TITLE_IDS[flow.state.step];
  const headerSubtitle = STEP_HEADER_SUBTITLE[flow.state.step] ?? '';
  const s = usePartnerSignupStyles();

  // Confirmation step is the post-success recap; X is hidden to keep the
  // CTA as the primary action. ENG-5251: ESC is globally disabled across
  // every step (see disableEscapeKeyDown below) — accidental key presses
  // were dropping users mid-signup. The confirmation step exits via the
  // primary CTA or backdrop click; both stay keyboard-reachable.
  const dialogOnClose = isTerminalStep ? onClose : handleClose;

  return (
    <Dialog
      open={open}
      onClose={dialogOnClose}
      disableEscapeKeyDown
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      classes={{ paper: s.paper }}
      slotProps={{
        // Blur whatever page mounted the modal so the user retains visual
        // context of where they were when signup was triggered (dashboard,
        // expert profile, etc.) instead of staring at a synthetic backdrop.
        backdrop: { sx: { backdropFilter: 'blur(8px)' } },
      }}
    >
      {/* Context bar — mirrors SMBSignupModal's partner side: a 44px round
          brand avatar on the left (replacing the expert picture), a
          two-line label in the middle (title + subtitle, mirroring
          "Viewing X / Title"), and a user-creation icon on the right
          (replacing the lock; this is signup, not booking). Close button
          replaces the context icon when the step is dismissible. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          pl: 3,
          pr: 4,
          bgcolor: 'var(--surfaceElevated)',
          borderBottom: '1px solid',
          borderColor: 'var(--surfaceBorder)',
        }}
      >
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: darkBrown,
            flexShrink: 0,
            // 3D button ring: outer drop shadow + inner highlight rim.
            // Matches the tactile look used on other OnFrontiers round
            // logos elsewhere in the app.
            boxShadow:
              '0 1px 2px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.12)',
          }}
        >
          {/* Inner mark sized to 70% of the avatar so the white letters
              have breathing room from the rim. */}
          <Box
            component="img"
            src="/images/icons/apple-touch-icon.png"
            alt="OnFrontiers"
            sx={{ width: '70%', height: '70%', objectFit: 'contain' }}
          />
        </Avatar>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            Customer Signup
          </Typography>
          {headerSubtitle && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontSize: 12,
                mt: '1px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {headerSubtitle}
            </Typography>
          )}
        </Box>
        <PersonAddOutlinedIcon sx={{ fontSize: 20, ml: 1.5, color: 'text.secondary' }} />
      </Box>
      <div aria-live="polite">
        {flow.state.step === 'account' && <AccountStep flow={flow} onSignup={onSignup} />}
        {flow.state.step === 'email-otp' && (
          <EmailOtpStep
            flow={flow}
            domainMatch={domainMatch}
            pendingProjectInvite={pendingProjectInvite}
            onSendEmailOtp={onSendEmailOtp}
            onVerifyEmailOtp={onVerifyEmailOtp}
          />
        )}
        {flow.state.step === 'invite-accept' && (
          <InviteAcceptStep flow={flow} onAcceptInvite={onAcceptInvite} />
        )}
        {flow.state.step === 'team-create' && (
          <TeamStep flow={flow} onRequestToJoin={onRequestToJoin} />
        )}
        {flow.state.step === 'team-join' && (
          <TeamJoinStep flow={flow} onRequestToJoin={onRequestToJoin} />
        )}
        {flow.state.step === 'team-request-sent' && (
          <TeamRequestSentStep flow={flow} onGoToDashboard={onGoToDashboard} ctaLabel={ctaLabel} />
        )}
        {flow.state.step === 'subscription' && (
          <Suspense fallback={<StepFallback />}>
            <SubscriptionStep
              flow={flow}
              trialDays={trialDays}
              seatCount={seatCount}
              onCreateSetupIntent={onCreateSetupIntent}
              onCompleteSMBSignup={onCompleteSMBSignup}
              onFetchPendingInvite={onFetchPendingInvite}
              onFetchActiveProjectMembership={onFetchActiveProjectMembership}
              onAcceptInvite={onAcceptInvite}
              onGoToProjects={onGoToProjects}
            />
          </Suspense>
        )}
        {flow.state.step === 'confirmation' && (
          <ConfirmationStep flow={flow} onGoToDashboard={onGoToDashboard} ctaLabel={ctaLabel} />
        )}
      </div>
      <ConfirmExitDialog
        open={confirmExitOpen}
        onCancel={handleCancelExit}
        onConfirm={handleConfirmExit}
      />
    </Dialog>
  );
}
